import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { API } from "@/lib/api";
import { track } from "@/lib/analytics";
import { useCurrency } from "@/lib/currency";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

/* ---- Callback modal (shared) ---- */
export function CallbackModal({ onClose }) {
  const { currency } = useCurrency();
  const [f, setF] = useState({ name: "", whatsapp: "", email: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || (!f.whatsapp && !f.email)) { setError("Name and a WhatsApp number or email are required."); setStatus("error"); return; }
    setStatus("sending");
    try {
      const response = await fetch(`${API}/leads`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...f, source: "callback", currency }) });
      if (!response.ok) throw new Error("Submission failed");
      track("lead_submit", { source: "callback" }); track("consultation_request", { source: "callback" }); setStatus("done");
    } catch { setError("We couldn't submit your request. Please check your details and try again."); setStatus("error"); }
  };
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="atlas-public z-[220]" style={md} data-testid="callback-modal">
        <DialogTitle className="sr-only">Request a sourcing callback</DialogTitle>
        <DialogDescription className="sr-only">Share your name and WhatsApp number or email so our team can respond.</DialogDescription>
        {status === "done" ? (
          <div style={{ textAlign: "center" }}><h3 style={{ color: "var(--navy)", fontSize: 22 }}>✓ We'll call you back</h3>
            <p style={{ color: "var(--grey)", marginTop: 8 }}>Our team responds within one business day.</p></div>
        ) : (
          <form onSubmit={submit}>
            <div className="fc-k" style={{ color: "#2E63AE" }}>FREE CONSULTATION</div>
            <h3 style={{ color: "var(--navy)", margin: "4px 0 14px" }}>Request a callback</h3>
            <input placeholder="Your name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} style={ip} data-testid="cb-name" />
            <input placeholder="WhatsApp number" value={f.whatsapp} onChange={(e) => setF({ ...f, whatsapp: e.target.value })} style={ip} data-testid="cb-whatsapp" />
            <input placeholder="Email (optional)" type="email" aria-label="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} style={ip} data-testid="cb-email" />
            {status === "error" && <p role="alert" data-testid="callback-error" style={{ color: "#a53b35", fontSize: 13 }}>{error}</p>}
            <button className="btn btn-red" style={{ width: "100%" }} disabled={status === "sending"} data-testid="cb-submit">{status === "sending" ? "Sending…" : "Request callback"}</button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ---- Sticky bottom bar ---- */
export function StickyBar() {
  const [show, setShow] = useState(false);
  const [modal, setModal] = useState(false);
  const [text, setText] = useState("Free consultation · China · India · Vietnam · Thailand");
  useEffect(() => {
    fetch(`${API}/settings`).then((r) => r.json()).then((d) => d.sticky_text && setText(d.sticky_text)).catch(() => {});
    if (sessionStorage.getItem("atlas_bar_dismissed")) return;
    const onScroll = () => {
      if (sessionStorage.getItem("atlas_bar_dismissed")) { setShow(false); return; }
      const p = window.scrollY / (document.body.scrollHeight - window.innerHeight || 1);
      setShow(p > 0.6);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    document.body.classList.toggle("bar-visible", show);
    return () => document.body.classList.remove("bar-visible");
  }, [show]);
  const dismiss = () => { sessionStorage.setItem("atlas_bar_dismissed", "1"); setShow(false); };
  return (
    <>
      <div className={"stickybar" + (show ? " show" : "")} data-testid="sticky-bar">
        <span className="txt">{text}</span>
        <span style={{ display: "flex", alignItems: "center" }}>
          <button className="btn btn-red" style={{ padding: "9px 18px" }} onClick={() => { setModal(true); track("cta_click", { cta: "sticky_bar" }); }} data-testid="sticky-cta">Free callback</button>
          <span className="x" onClick={dismiss} data-testid="sticky-dismiss">✕</span>
        </span>
      </div>
      {modal && <CallbackModal onClose={() => setModal(false)} />}
    </>
  );
}

/* ---- Exit-intent (desktop, once/session) ---- */
export function ExitIntent() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (window.__PRERENDER__ || window.innerWidth < 900 || sessionStorage.getItem("atlas_exit_shown")) return;
    const onLeave = (e) => {
      if (e.clientY <= 0) { setShow(true); sessionStorage.setItem("atlas_exit_shown", "1"); document.removeEventListener("mouseout", onLeave); }
    };
    document.addEventListener("mouseout", onLeave);
    return () => document.removeEventListener("mouseout", onLeave);
  }, []);
  if (!show) return null;
  return (
    <div style={ov} onClick={() => setShow(false)}>
      <div style={md} onClick={(e) => e.stopPropagation()} data-testid="exit-intent">
        <div className="fc-k" style={{ color: "#2E63AE" }}>BEFORE YOU GO</div>
        <h3 style={{ color: "var(--navy)", margin: "4px 0 8px", fontSize: 22 }}>Get the free China Sourcing Atlas</h3>
        <p style={{ color: "var(--grey)", marginBottom: 16 }}>25 markets, 12 specialty towns, Yiwu district by district — the guide importers actually use.</p>
        <Link className="btn btn-red" style={{ width: "100%", textAlign: "center" }} to="/guides/china-sourcing-atlas" onClick={() => setShow(false)} data-testid="exit-cta">Read it free →</Link>
      </div>
    </div>
  );
}

/* ---- Reading progress bar ---- */
export function ProgressBar() {
  const [w, setW] = useState(0);
  useEffect(() => {
    const onScroll = () => setW((window.scrollY / (document.body.scrollHeight - window.innerHeight || 1)) * 100);
    window.addEventListener("scroll", onScroll, { passive: true }); onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return <div className="progressbar" style={{ width: `${Math.min(100, w)}%` }} data-testid="progress-bar" />;
}

/* ---- Related content strip ---- */
export function RelatedStrip({ items }) {
  if (!items?.length) return null;
  return (
    <section className="related">
      <div className="wrap">
        <div className="k2">KEEP READING</div>
        <div className="rgrid">
          {items.map((it, i) => (
            <Link key={i} to={it.to} data-testid={`related-link-${i}`}><span className="rk">{it.kicker}</span><h4>{it.title}</h4></Link>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---- "How we're paid" trust block ---- */
export function HowPaid() {
  return (
    <div className="howpaid" id="how-were-paid" data-testid="how-were-paid">
      <h3>How we're paid</h3>
      <p style={{ color: "var(--grey)", fontSize: 14.5 }}>Straight answer to a fair question about sourcing agents:</p>
      <ul>
        <li>We buy direct from an IATF 16949–certified fastener plant — no trader mark-ups.</li>
        <li>Our commission is disclosed up front and quoted per brief — never hidden inside the product price.</li>
        <li>We take no undisclosed payments or kickbacks from suppliers.</li>
        <li>Our verification and inspection work for the buyer — your interests, not the factory's.</li>
      </ul>
    </div>
  );
}

export const HOW_PAID_FAQ = { q: "How is The Sourcing Atlas paid, and do you take money from suppliers?", a: "Our sourcing commission is disclosed up front and quoted per brief — never hidden inside the product price. We take no undisclosed payments or kickbacks from suppliers; our verification and inspection work for the buyer." };

const ov = { position: "fixed", inset: 0, background: "rgba(9,25,55,.55)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 };
const md = { background: "#fff", borderRadius: 16, padding: 28, width: 400, maxWidth: "100%", boxShadow: "0 26px 70px rgba(9,25,55,.4)" };
const ip = { width: "100%", border: "1.5px solid var(--line)", borderRadius: 10, padding: "12px 13px", fontSize: 14, marginBottom: 10, fontFamily: "inherit", background: "#FCFDFF" };
